import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  LinkOutlined,
  RefreshOutlined,
} from "@material-ui/icons";
import { ExpandCircleDown } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useConfirmation from "../../../hooks/useConfirmation";
import useToast from "../../../hooks/useToast";
import {
  evaluation_refonte_fiche_evaluation_service,
  evaluation_refonte_jalon_service,
  evaluation_refonte_questionnaire_par_fiche_service,
} from "../../../services/PersonalEvaluationRefonteService";
import { evaluation_utilities_service } from "../../../services/PersonalEvaluationService";
import CategoryCritereForm from "../category_criteria/CategoryCritereForm";
import AssignerCritereToFiche from "../evaluation_criteria/AssignerCritereToFiche";
import CritereForm from "../evaluation_criteria/CritereForm";
import AutocompleteCombobox, {
  adaptDataValueToAutocompleteData,
} from "../shared/AutocompleteCombobox";
import FicheLoader from "./loader/FicheLoader";

const INIT_CURRENT_QUESTION = {
  ID_QUESTIONNAIRE: 0,
  ID_FICHE_EVALUATION: 0,
  ID_CATEGORY: 0,
  ID_TYPE_QUESTIONNAIRE: 0,
  NOTE_MIN: 0,
  NOTE_MAX: 0,
  CREATION_DATE: null,
  QUESTION: "",
  IS_COMMON: "0",
};

const INIT_SELECTED_CATEGORY_CRITERE = {
  ID_CATEGORY: 0,
  CATEGORY_NAME: "",
  IS_COMMON: "0",
  ID_POSTE: 0,
  CREATION_DATE: "",
};

const INIT_SELECTED_FICHER_EVALUATION = {
  ID_FICHE_EVALUATION: 0,
  LIBELE_FICHE: "",
  ID_POSTE: 0,
  ACTIVE: "1",
};

const FicheEvaluationForm = ({ isOpen, ficheEvaluation, handleClose }) => {
  const { showToast, Toast } = useToast();
  const { DialogComponent, openConfirmDialog } = useConfirmation();
  const [jalonReponses, setJalonReponses] = useState([]);
  const [fonctions, setFonctionsList] = useState([]);
  const [details, setDetails] = useState([]);
  const [loadsaveFiche, setLoadsaveFiche] = useState(false);
  const [selectedFicheEvaluation, setSelectedFicheEvaluation] = useState(
    INIT_SELECTED_FICHER_EVALUATION,
  );

  const [formLoad, setFormLoad] = useState(false);
  const [categoryCritereFormOpen, setCategoryCritereFormOpen] = useState(false);
  const [critereFormOpen, setCritereFormOpen] = useState(false);
  const [current_question, setCurrentQuestion] = useState(
    INIT_CURRENT_QUESTION,
  );
  const [selectedCategoryCritere, setCategoryCritere] = useState(
    INIT_SELECTED_CATEGORY_CRITERE,
  );
  const [formAssignationOpen, setFormAssignationOpen] = useState(false);

  const validationErrors = useMemo(
    () => ({
      fonctionError:
        selectedFicheEvaluation.ID_POSTE === 0 ||
        !selectedFicheEvaluation.ID_POSTE
          ? "La fonction/poste doit etre mentionné"
          : null,
      libelrError:
        !selectedFicheEvaluation.LIBELE_FICHE ||
        String(selectedFicheEvaluation.LIBELE_FICHE).trim() === ""
          ? "Veuillez labeliser cette fiche"
          : null,
    }),
    [selectedFicheEvaluation.ID_POSTE, selectedFicheEvaluation.LIBELE_FICHE],
  );

  const fonctionOptions = useMemo(
    () => adaptDataValueToAutocompleteData(fonctions, "name", "function_id"),
    [fonctions],
  );

  const selectedFonctionValue = useMemo(
    () =>
      fonctionOptions.find(
        (item) => item.function_id === selectedFicheEvaluation.ID_POSTE,
      ),
    [fonctionOptions, selectedFicheEvaluation.ID_POSTE],
  );

  const groupedDetails = useMemo(() => {
    const categories = Array.from(
      new Set((details || []).map((dt) => dt.CATEGORY_NAME).filter(Boolean)),
    );

    return categories.map((category) => ({
      category,
      items: details.filter((dt) => dt.CATEGORY_NAME === category),
    }));
  }, [details]);

  const handleRefreshJalonListByFIcheEvaluation = useCallback((id_fiche) => {
    evaluation_refonte_jalon_service.getListByFiche(id_fiche).then((res) => {
      if (res && res.data) {
        setJalonReponses(res.data || []);
      }
    });
  }, []);

  const handleLoadAllDetails = useCallback(
    (fiche) => {
      const idFiche = fiche?.ID_FICHE_EVALUATION ?? 0;
      if (!idFiche) {
        setDetails([]);
        setJalonReponses([]);
        setFormLoad(false);
        return;
      }

      evaluation_refonte_fiche_evaluation_service
        .getAllDetail(idFiche)
        .then((res) => {
          if (res) {
            setDetails(res.data || []);
          }
        })
        .finally(() => {
          setFormLoad(false);
        });

      handleRefreshJalonListByFIcheEvaluation(idFiche);
    },
    [handleRefreshJalonListByFIcheEvaluation],
  );

  const handleCloseAssignationForm = () => {
    handleLoadAllDetails(selectedFicheEvaluation);
    setFormAssignationOpen(false);
  };

  const getInsertableCurrentFicheEvaluation = () => {
    return {
      ID_FICHE_EVALUATION: selectedFicheEvaluation.ID_FICHE_EVALUATION,
      LIBELE_FICHE: selectedFicheEvaluation.LIBELE_FICHE,
      ID_POSTE: selectedFicheEvaluation.ID_POSTE,
      ACTIVE: selectedFicheEvaluation.ACTIVE,
    };
  };

  const handleDialogClose = () => {
    handleClose();
    handleClearAllField();
  };

  const isAllFieldOk = useCallback(() => {
    return Object.values(validationErrors).every((value) => value === null);
  }, [validationErrors]);

  const handleSaveFicheEvaluation = (close = false) => {
    if (isAllFieldOk()) {
      if (getInsertableCurrentFicheEvaluation().ID_FICHE_EVALUATION > 0) {
        handleUpdateCritereGroupee(close);
      } else {
        handleInsertCritereGroupee(close);
      }
    } else {
      showToast(
        TYPE_NOTIFICATION.ERROR,
        "Veuillez verifier les champs de saisi",
      );
    }
  };

  const handleInsertCritereGroupee = (close = false) => {
    setLoadsaveFiche(true);
    evaluation_refonte_fiche_evaluation_service
      .insert(getInsertableCurrentFicheEvaluation())
      .then((res) => {
        if (res && res.success) {
          if (close) {
            handleDialogClose();
          } else {
            setSelectedFicheEvaluation(res.data);
            handleLoadAllDetails(res.data);
          }
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setLoadsaveFiche(false);
      });
  };
  const handleUpdateCritereGroupee = (close = false) => {
    setLoadsaveFiche(true);
    evaluation_refonte_fiche_evaluation_service
      .update(getInsertableCurrentFicheEvaluation())
      .then((res) => {
        if (res && res.success) {
          if (close) {
            handleDialogClose();
            handleClearAllField();
          } else {
            setSelectedFicheEvaluation(res.data);
            handleLoadAllDetails(res.data);
            //ouvrir le fenetre nouvelle question
          }
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setLoadsaveFiche(false);
      });
  };

  const handleClearAllField = useCallback(() => {
    setSelectedFicheEvaluation(INIT_SELECTED_FICHER_EVALUATION);
    setCurrentQuestion(INIT_CURRENT_QUESTION);
    setCategoryCritere(INIT_SELECTED_CATEGORY_CRITERE);
    setDetails([]);
    setJalonReponses([]);
    setFormLoad(false);
  }, []);

  const handleChangeCritereGroupeActuel = (evt) => {
    const { name, value } = evt.target;
    setSelectedFicheEvaluation((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoadFuncitonList = useCallback(() => {
    evaluation_utilities_service.getFonctionList(null).then((res) => {
      if (res && res.success) {
        setFonctionsList(res.data);
      }
    });
  }, []);

  const handleChangeActiveStatus = (evt) => {
    const misokatra = evt.target.checked;
    if (misokatra) {
      openConfirmDialog(
        "Confirmation",
        "Voullez vous activer ce fiche d'evaluation? les autres fiches d'evaluatoin de meme poste seront desactivés.",
        () => {
          setSelectedFicheEvaluation((prev) => ({
            ...prev,
            ACTIVE: misokatra ? "1" : "0",
          }));
        },
        () => {},
      );
    } else {
      setSelectedFicheEvaluation((prev) => ({
        ...prev,
        ACTIVE: misokatra ? "1" : "0",
      }));
    }
  };

  const handleCloseCategoryCritereForm = () => {
    handleLoadAllDetails(selectedFicheEvaluation);
    setCategoryCritereFormOpen(false);
  };
  const handleOpenCategoryCritereForm = (category_critere) => {
    setCategoryCritere(category_critere);
    setCategoryCritereFormOpen(true);
  };

  const handleChangeFonciton = (val) => {
    setSelectedFicheEvaluation((prev) => ({
      ...prev,
      ID_POSTE: val?.function_id || null,
    }));
  };

  const handleCritereFormClose = () => {
    handleLoadAllDetails(selectedFicheEvaluation);
    setCritereFormOpen(false);
    setCurrentQuestion(INIT_CURRENT_QUESTION);
  };

  const handleCritereFormOpen = (critere) => {
    setCurrentQuestion(critere);
    setCritereFormOpen(true);
  };

  const handleAddNewQuestoinnaire = () => {
    if (getInsertableCurrentFicheEvaluation().ID_FICHE_EVALUATION === 0) {
      openConfirmDialog(
        "Confirmation",
        "Cette fiche d'evalutation n'est pas encore enregistré, voullez vous l'enregistrer d'abord?",
        () => {
          handleSaveFicheEvaluation(false);
        },
        () => {},
      );
    } else {
      let currentQuestion = INIT_CURRENT_QUESTION;
      currentQuestion = {
        ...currentQuestion,
        ID_FICHE_EVALUATION: selectedFicheEvaluation.ID_FICHE_EVALUATION,
      };
      handleCritereFormOpen(currentQuestion);
    }
  };

  const handleEditQuestionnaire = (questionaire) => {
    setCurrentQuestion(questionaire);
    setCritereFormOpen(true);
  };

  const handleOpenAssignQuestionnaire = () => {
    if (getInsertableCurrentFicheEvaluation().ID_FICHE_EVALUATION === 0) {
      openConfirmDialog(
        "Confirmation",
        "Cette fiche d'evalutation n'est pas encore enregistré, voullez vous l'enregistrer d'abord?",
        () => {
          handleSaveFicheEvaluation(false);
        },
        () => {},
      );
    } else {
      setFormAssignationOpen(true);
    }
  };

  const handleConfirmDeleteQuestionFromFiche = (id_quesition) => {
    evaluation_refonte_questionnaire_par_fiche_service
      .delete(id_quesition)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleLoadAllDetails(selectedFicheEvaluation);
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      });
  };

  const handleDeleteQuestionnFromFiche = (id_quesition) => {
    openConfirmDialog(
      "Confirmation",
      "Voullez vous supprimer cette ligne?",
      () => handleConfirmDeleteQuestionFromFiche(id_quesition),
      () => {},
    );
  };

  const handleInitAll = useCallback(() => {
    setCurrentQuestion(INIT_CURRENT_QUESTION);
    setSelectedFicheEvaluation(INIT_SELECTED_FICHER_EVALUATION);
    setCategoryCritere(INIT_SELECTED_CATEGORY_CRITERE);
    setDetails([]);
    setJalonReponses([]);
    setFormLoad(false);
  }, []);

  useEffect(() => {
    handleInitAll();

    if (!isOpen) {
      return;
    }

    const nextFiche = ficheEvaluation || INIT_SELECTED_FICHER_EVALUATION;
    if (nextFiche.ID_FICHE_EVALUATION > 0) {
      setFormLoad(true);
    }
    handleLoadAllDetails(nextFiche);
    setSelectedFicheEvaluation(nextFiche);
    handleLoadFuncitonList();
  }, [
    ficheEvaluation,
    isOpen,
    handleInitAll,
    handleLoadAllDetails,
    handleLoadFuncitonList,
  ]);

  const ButtonNewAssignQuestionnaireDisabled = memo(
    ({
      btn1 = "Nouvelle question",
      variant1 = "contained",
      btn2 = "Assigner",
      variant2 = "contained",
    }) => {
      return (
        <>
          <LoadingButton
            variant={variant1}
            size="small"
            onClick={(e) => handleAddNewQuestoinnaire()}
            startIcon={<AddOutlined />}
            loading={loadsaveFiche}
          >
            {btn1}
          </LoadingButton>
          <LoadingButton
            sx={{ ml: 1 }}
            size="small"
            variant={variant2}
            onClick={(e) => handleOpenAssignQuestionnaire()}
            startIcon={<LinkOutlined />}
            loading={loadsaveFiche}
          >
            {btn2}
          </LoadingButton>
        </>
      );
    },
  );

  return (
    <>
      <Toast />
      <DialogComponent />
      <AssignerCritereToFiche
        current_fiche={selectedFicheEvaluation}
        handle_close={handleCloseAssignationForm}
        isOpen={formAssignationOpen}
      />
      <Dialog
        open={isOpen}
        /* onClose={ handleDialogClose} */
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        fullWidth
        maxWidth="lg"
      >
        <CritereForm
          current_critere={current_question}
          current_fiche={selectedFicheEvaluation}
          handleClose={handleCritereFormClose}
          isOpen={critereFormOpen}
        />
        <CategoryCritereForm
          current_category_critere={selectedCategoryCritere}
          isOpen={categoryCritereFormOpen}
          handleClose={handleCloseCategoryCritereForm}
        />
        <FicheLoader show={formLoad} />
        <Paper sx={{ display: formLoad ? "none" : "block" }}>
          <Grid container display={"flex"} flexDirection={"column"}>
            <Grid m={2} display={"flex"} flexDirection={"row"}>
              <Grid alignContent={"center"}>
                <Typography variant="h6">
                  {selectedFicheEvaluation.ID_FICHE_EVALUATION === 0
                    ? "Nouvelle fiche d'évaluation"
                    : "Modifier fiche d'évaluation"}
                </Typography>
              </Grid>
              <Grid flexGrow={1}>
                <DialogActions>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedFicheEvaluation.ACTIVE === "1"}
                        onChange={(e) => handleChangeActiveStatus(e)}
                        name="gilad"
                      />
                    }
                    label={
                      selectedFicheEvaluation.ACTIVE === "1"
                        ? "Active"
                        : "Inactive"
                    }
                  />
                </DialogActions>
              </Grid>
            </Grid>
            <Divider />
            <DialogContent
              id="dialog-description"
              className="app-scroll-bar-view"
            >
              <Grid container flexDirection={"row"} mt={0.5} overflow={"auto"}>
                <Grid item md={6}>
                  <FormControl fullWidth>
                    <FormLabel>Libelé *</FormLabel>
                    <TextField
                      sx={{ mt: 1.25 }}
                      size="small"
                      fullWidth
                      label={"Libelé du fiche d'évaluation *"}
                      name="LIBELE_FICHE"
                      value={selectedFicheEvaluation.LIBELE_FICHE}
                      onChange={(e) => handleChangeCritereGroupeActuel(e)}
                      error={Boolean(validationErrors.libelrError)}
                      helperText={
                        validationErrors.libelrError
                          ? validationErrors.libelrError
                          : ""
                      }
                    ></TextField>
                  </FormControl>
                </Grid>
                <Grid item md={6}>
                  <FormControl fullWidth>
                    <FormLabel>Fonction *</FormLabel>
                    <AutocompleteCombobox
                      fullWidth
                      stylex={{ width: "400px" }}
                      error={Boolean(validationErrors.fonctionError)}
                      helperText={
                        validationErrors.fonctionError
                          ? validationErrors.fonctionError
                          : ""
                      }
                      limitTags={2}
                      label="Titre du poste à evaluer *"
                      class_name="filtre_list"
                      valeur={selectedFonctionValue}
                      data={fonctionOptions}
                      onChange={handleChangeFonciton}
                    />
                  </FormControl>
                </Grid>
                <Grid className="contenu" width={"100%"} minHeight={"40vh"}>
                  <Grid display={"flex"} flexDirection={"row"}>
                    <Grid flexGrow={1}>
                      <CardHeader
                        title={"Categories et critères"}
                        sx={{ mb: -2 }}
                        titleTypographyProps={{
                          fontSize: "18px",
                          fontWeight: "bold",
                        }}
                      />
                    </Grid>
                    <Grid
                      m={1}
                      className="category"
                      display={"flex"}
                      justifyContent={"flex-end"}
                      alignItems={"center"}
                    >
                      <Button
                        variant="text"
                        onClick={(e) =>
                          handleLoadAllDetails(selectedFicheEvaluation)
                        }
                      >
                        <RefreshOutlined />
                      </Button>
                      {<ButtonNewAssignQuestionnaireDisabled />}
                    </Grid>
                  </Grid>
                  <Divider variant="fullWidth" />
                  <Grid mt={1} maxHeight={"50vh"} overflow={"auto"}>
                    {groupedDetails.length === 0 && (
                      <Grid
                        display={"flex"}
                        mt={10}
                        justifyContent={"center"}
                        width={"100%"}
                      >
                        <Alert
                          variant="outlined"
                          severity="info"
                          sx={{ width: "50%", textAlign: "center" }}
                        >
                          <AlertTitle>Aucune information disponible</AlertTitle>
                          Veuillez ajouter une nouvelle question ou assigner des
                          questions existantes
                          <br />
                          <br />
                          <ButtonNewAssignQuestionnaireDisabled btn1="Ajouter" />
                        </Alert>
                      </Grid>
                    )}
                    {groupedDetails.map(({ category, items }, idx) => (
                      <Fragment key={category}>
                        <Grid key={idx} display={"flex"} flexDirection={"row"}>
                          <Grid>
                            <CardHeader
                              title={`${idx + 1}. ${category}`}
                              titleTypographyProps={{
                                variant: "body1",
                                color: "blue",
                                fontSize: "20px",
                              }}
                            />
                          </Grid>
                          <Grid display={"none"} alignItems={"center"}>
                            <Button
                              color="info"
                              size="small"
                              onClick={(e) =>
                                handleOpenCategoryCritereForm(
                                  selectedCategoryCritere,
                                )
                              }
                            >
                              <EditOutlined />
                            </Button>
                            <Button color="warning" size="small">
                              <DeleteOutline />
                            </Button>
                          </Grid>
                        </Grid>
                        {items.map((detail, dt_idx) => (
                          <Accordion key={dt_idx}>
                            <AccordionSummary
                              expandIcon={
                                String(detail.TYPE_NAME)
                                  .toLowerCase()
                                  .includes("jalon") ? (
                                  <ExpandCircleDown />
                                ) : (
                                  ""
                                )
                              }
                              aria-controls="panel1-content"
                              id="panel1-header"
                            >
                              <Grid
                                display={"flex"}
                                width={"100%"}
                                flexDirection={"row"}
                              >
                                <Grid flexGrow={1}>
                                  <Typography variant="h6">
                                    {`${dt_idx + 1}. ` + detail.QUESTION}
                                  </Typography>
                                  <Grid
                                    container
                                    display={"flex"}
                                    flexDirection={"row"}
                                    spacing={2}
                                  >
                                    <Grid item>
                                      <Typography variant="caption">
                                        Type :
                                        <span>
                                          {detail.TYPE_NAME}
                                          {String(detail.TYPE_NAME)
                                            .toLowerCase()
                                            .includes("note")
                                            ? ` (de ${detail.NOTE_MIN} à ${detail.NOTE_MAX})`
                                            : ``}
                                        </span>
                                      </Typography>
                                    </Grid>
                                    <Grid item textAlign={"center"}>
                                      <Typography variant="caption">
                                        Crée le :{" "}
                                        <span>{detail.CREATION_DATE}</span>
                                        {/* <span>{new Date(detail.CREATION_DATE).toLocaleTimeString()}</span> */}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid display={"flex"} flexDirection={"row"}>
                                  <Tooltip title="Cliquer pour modifier">
                                    <Button
                                      color="info"
                                      size="small"
                                      onClick={(e) =>
                                        handleEditQuestionnaire({
                                          ID_QUESTIONNAIRE:
                                            detail.ID_QUESTIONNAIRE,
                                          ID_FICHE_EVALUATION:
                                            detail.ID_FICHE_EVALUATION,
                                          ID_CATEGORY: detail.ID_CATEGORY,
                                          ID_TYPE_QUESTIONNAIRE:
                                            detail.ID_TYPE_QUESTIONNAIRE,
                                          NOTE_MIN: detail.NOTE_MIN,
                                          NOTE_MAX: detail.NOTE_MAX,
                                          QUESTION: detail.QUESTION,
                                        })
                                      }
                                    >
                                      <EditOutlined />
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Cliquer pour supprimer">
                                    <Button
                                      color="warning"
                                      size="small"
                                      onClick={(e) =>
                                        handleDeleteQuestionnFromFiche(
                                          detail.ID_QUESTIONNAIRE,
                                        )
                                      }
                                    >
                                      <DeleteOutline />
                                    </Button>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                display: String(detail.TYPE_NAME).includes(
                                  "jalon",
                                )
                                  ? ""
                                  : "none",
                              }}
                            >
                              <Divider variant="middle" />
                              <Grid>
                                <CardHeader
                                  title={"Liste des reponses jalon"}
                                  sx={{ mb: -2 }}
                                  titleTypographyProps={{
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                  }}
                                />
                                <Grid container m={0.5}>
                                  {jalonReponses
                                    .filter(
                                      (item) =>
                                        item.ID_QUESTIONNAIRE ===
                                        detail.ID_QUESTIONNAIRE,
                                    )
                                    .map((item, idx) => (
                                      <>
                                        <Paper
                                          key={idx}
                                          elevation={2}
                                          sx={{ m: 0.5 }}
                                        >
                                          <Grid
                                            key={idx}
                                            p={1}
                                            display={"flex"}
                                            flexDirection={"row"}
                                          >
                                            <Grid
                                              flexGrow={1}
                                              display={"flex"}
                                              alignContent={"center"}
                                            >
                                              {`${idx + 1}. ${item.VALEUR}`}
                                            </Grid>
                                          </Grid>
                                        </Paper>
                                      </>
                                    ))}
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}

                        <Grid
                          m={1}
                          className="category"
                          display={"none"}
                          justifyContent={"flex-end"}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddOutlined />}
                          >
                            Ajouter question
                          </Button>
                        </Grid>
                      </Fragment>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions>
              <LoadingButton
                loading={false}
                size="small"
                className="form_control"
                type="submit"
                onClick={(e) => handleSaveFicheEvaluation(true)}
                variant="contained"
              >
                Enregistrer{" "}
              </LoadingButton>
              <Button
                className="form_control"
                size="small"
                onClick={handleDialogClose}
                variant="outlined"
              >
                Fermer
              </Button>
            </DialogActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default FicheEvaluationForm;
